/**
 * Ventas.
 *
 * The busiest reducer in the system, and the one that has to be right about four things at
 * once: stock goes down, cost of sales is posted at the average in effect, every payment lands
 * in the correct account, and the whole thing balances.
 *
 * Note what a sale does NOT do: it does not change the weighted average. It removes quantity
 * at the running average and leaves the average where it was. That is what makes a sale safe
 * to reverse at any time — the goods can go back at the cost they left with, whatever happened
 * in between. A purchase does not have that property, which is why annulling one is restricted.
 */

import {
  ZERO,
  ZERO_COST,
  ZERO_QTY,
  addMoney,
  addQty,
  centavos,
  costOf,
  fechaEnNicaragua,
  formatMoney,
  formatQuantity,
  fxRate,
  lineTotal,
  orderKey,
  saleToBase,
  subMoney,
  subQty,
  usdToCordobas,
  weightedAverageCost,
  type AnyDomainEvent,
  type Centavos,
  type Milli,
  type UnitCost,
  type CuentaBanco,
} from '@abastece/shared';
import { credit, debit, entry, type JournalLine } from '../ledger/journal.js';
import { DONDE_SE_APARTA, apartadoPorProducto, factoresEnLibros, libre } from '../apartados.js';
import { adondeVuelve, ajusteVendidoAntes, consumirLotesFefo, cuentaInventario, devolverLotes, yaContado, type Recibido } from '../inventario.js';
import { confirmarPagoRecarga, rechazarPagoRecarga } from './recargas.js';
import { bajarDeuda, confirmaTransferencias, confirmarPagoAbono, rechazarPagoAbono } from './fiado.js';
import { cuentaCerradaParaSiempre, cuentaDeTransferencia } from './bancos.js';
import { diaDeLaConfirmacion } from './contadas.js';
import type { AccountKey } from '../ledger/accounts.js';
import {
  efectivoDeVenta,
  existenciaId,
  fiadoDeVenta,
  type ApplyResult,
  type Effect,
  type Reader,
  type Venta,
  type VentaLinea,
  type VentaPago,
  type FormaPagoVenta,
} from '../state.js';

function cuentaDePago(forma: FormaPagoVenta, estado: string, cuenta?: string | null): AccountKey {
  switch (forma) {
    case 'efectivo_cordobas':
      return 'CAJA_CORDOBAS';
    case 'efectivo_dolares':
      return 'CAJA_DOLARES';
    case 'transferencia':
      // Unverified money is not in the bank yet. It waits in a holding account with a name
      // that says so, and shows up on a queue somebody has to clear.
      // Seen in the bank: the account it was seen in (`bancos.ts`), «Bancos» unless it says.
      return estado === 'pendiente' ? 'TRANSFERENCIAS_PENDIENTES' : cuentaDeTransferencia(cuenta as CuentaBanco | null | undefined);
    case 'billetera':
      // A wallet screenshot is no more a payment than a bank one: same queue, same holding account.
      return estado === 'pendiente' ? 'TRANSFERENCIAS_PENDIENTES' : 'BILLETERA_MOVIL';
    case 'tarjeta':
      // The bank the card terminal pays into (`cuentaTarjeta`), written on the payment.
      return cuentaDeTransferencia(cuenta as CuentaBanco | null | undefined);
    case 'credito':
      // Fiado: no money, a debt. It stays in cuentas por cobrar until an abono moves it out.
      return 'CUENTAS_POR_COBRAR';
  }
}

export async function reduceVenta(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  switch (event.type) {
    case 'venta.registrada':
      return registrar(event, read, orderKey(event));
    case 'venta.anulada':
      return anular(event, read, orderKey(event));
    case 'pago.confirmado':
      return confirmarPago(event, read, orderKey(event));
    case 'pago.rechazado':
      return rechazarPago(event, read, orderKey(event));
    default:
      return null;
  }
}

/**
 * The first product a sale takes more of than is free: what the shop holds, less what pending orders
 * set aside when they do (`apartarPedidos`), the sale's own order aside. Said as the till says it;
 * null when everything is there.
 */
async function noHay(
  p: Extract<AnyDomainEvent, { type: 'venta.registrada' }>['payload'],
  read: Reader,
  apartar: boolean,
): Promise<string | null> {
  const lleva = new Map<string, Milli>();
  for (const l of p.lineas) {
    if (!l.productoId) continue;
    lleva.set(l.productoId, addQty(lleva.get(l.productoId) ?? ZERO_QTY, saleToBase(l.cantidad as Milli, l.factor as Milli)));
  }
  if (lleva.size === 0) return null;
  let apartado = new Map<string, Milli>();
  if (apartar) {
    const pendientes = await read.pedidosPendientes();
    const factorDe = await factoresEnLibros(read, pendientes.flatMap((x) => x.lineas.map((l) => l.unidadVentaId)));
    apartado = apartadoPorProducto(pendientes, factorDe, p.pedidoId);
  }
  for (const [productoId, cantidad] of lleva) {
    const hay = (await read.existencia(productoId, DONDE_SE_APARTA))?.cantidad ?? ZERO_QTY;
    const deOtros = apartado.get(productoId) ?? ZERO_QTY;
    const queda = libre(hay, deOtros);
    if (cantidad <= queda) continue;
    const nombre = (await read.producto(productoId))?.nombre ?? p.lineas.find((l) => l.productoId === productoId)?.descripcion ?? '';
    return deOtros > 0
      ? `No hay suficiente ${nombre}: quedan ${formatQuantity(queda)} sin apartar y la venta lleva ${formatQuantity(cantidad)}`
      : `No hay suficiente ${nombre}: quedan ${formatQuantity(queda)} y la venta lleva ${formatQuantity(cantidad)}`;
  }
  return null;
}

async function registrar(
  event: Extract<AnyDomainEvent, { type: 'venta.registrada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  if (await read.venta(p.ventaId)) {
    // The copy of a sale whose answer never reached the till (`respaldo`): the sale stood, so it is nothing.
    if (p.respaldo) return { effects: [], rejections: [] };
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa venta ya fue registrada' }] };
  }

  const sesion = await read.sesionCaja(p.sesionId);
  if (!sesion) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'No hay una caja abierta para esta venta' }] };
  }
  if (sesion.estado === 'cerrada') {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja de esta venta ya fue cerrada' }] };
  }

  /**
   * No pay, no goods — enforced here rather than in the screen. What is tendered, less the
   * change given back, has to equal the total. Fiado is one of the tenders: what the customer
   * leaves owing is a `credito` payment in this same sale, posted to receivables and checked
   * below.
   */
  const tendido = p.pagos.reduce((a, x) => a + x.monto, 0);
  if (tendido - p.cambio !== p.total) {
    return {
      effects: [],
      rejections: [
        {
          eventId: event.id,
          reason: `Los pagos no cuadran con el total: recibido ${tendido - p.cambio}, total ${p.total}`,
        },
      ],
    };
  }

  /**
   * The device's arithmetic, checked rather than trusted: each line is its quantity times its
   * price, the total is the lines less the discount plus the delivery plus the rounding, and the
   * rounding is no bigger than rounding to the shop's coin. A total that does not add up would otherwise post an
   * entry that does not balance — and a «rounding» of fifty córdobas is a discount nobody gave.
   * Half of C$ 1 at least, so a phone that sold offline before the step was made finer still gets in.
   */
  const config = await read.config();
  for (const pago of p.pagos) {
    const cerrada = cuentaCerradaParaSiempre(config, pago.cuenta);
    if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
  }
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  for (const l of p.lineas) {
    const esperado = lineTotal(centavos(l.precioUnitario), l.cantidad as Milli);
    if (l.subtotal !== esperado) {
      return rechazo(
        `La línea «${l.descripcion}» no cuadra: dice ${formatMoney(centavos(l.subtotal))} y su cantidad por su precio da ${formatMoney(esperado)}`,
      );
    }
  }
  const lineasSuman = p.lineas.reduce((a, l) => a + l.subtotal, 0);
  if (p.descuento > lineasSuman) return rechazo('El descuento es más grande que la venta');
  const daria = lineasSuman - p.descuento + p.envio + p.redondeo;
  if (p.total !== daria) {
    return rechazo(
      `El total no cuadra con las líneas${p.envio > 0 ? ' y el envío' : ''}: dice ${formatMoney(centavos(p.total))} y da ${formatMoney(centavos(daria))}`,
    );
  }
  if (Math.abs(p.redondeo) > Math.ceil(Math.max(config.cajaRedondeoCentavos, 100) / 2)) {
    return rechazo(`Un redondeo de ${formatMoney(centavos(p.redondeo))} es más de lo que redondea la caja`);
  }

  /**
   * Change comes out of the drawer, so only cash can have any. Change against a transfer or a
   * wallet payment is cash handed over for money nobody has seen yet — and gone for good if the
   * screenshot was fake.
   */
  const efectivoTendido = p.pagos.reduce(
    (a, x) => (x.forma === 'efectivo_cordobas' || x.forma === 'efectivo_dolares' ? a + x.monto : a),
    0,
  );
  if (p.cambio > efectivoTendido) {
    return {
      effects: [],
      rejections: [
        { eventId: event.id, reason: 'El vuelto sale del efectivo: no puede ser más que el efectivo recibido' },
      ],
    };
  }

  /**
   * Fiado needs somebody to owe it. The limit is not checked here: the goods left with the
   * client on a phone that may have been offline, and refusing the sale now would only lose
   * the record of a debt that exists. The till checks the limit, and asks for the owner's PIN
   * past it; whoever allowed it rides on the payment.
   */
  const fiado = p.pagos.reduce((a, x) => (x.forma === 'credito' ? a + x.monto : a), 0);
  let cliente = null;
  if (fiado > 0) {
    if (!p.clienteId) {
      return { effects: [], rejections: [{ eventId: event.id, reason: 'Una venta al fiado necesita el cliente' }] };
    }
    cliente = await read.cliente(p.clienteId);
    if (!cliente) {
      return { effects: [], rejections: [{ eventId: event.id, reason: 'El cliente del fiado no existe' }] };
    }
    if (p.pagos.some((x) => x.forma === 'credito' && x.estado !== 'confirmado')) {
      return { effects: [], rejections: [{ eventId: event.id, reason: 'El fiado no puede quedar pendiente' }] };
    }
  }

  /**
   * The order it delivers, while it is still pending. One cancelled on another phone meanwhile, or
   * delivered by another sale, or never taken by the server, is left as it is, and the sale stands:
   * the goods have left, and a sale is never refused for that (2026-10-13). It was refused, and
   * the goods, the cash and the client's debt vanished from the books with it, while the route's
   * settlement booked them short against the driver.
   */
  let pedido = null;
  if (p.pedidoId) {
    const delPedido = await read.pedido(p.pedidoId);
    if (delPedido?.estado === 'pendiente') pedido = delPedido;
  }

  /**
   * Payments the books can hold, and only those (2026-10-13). Dollars say how many dollars, or the
   * dollar drawer is booked córdobas it never got. Only a transfer or a wallet payment waits to be
   * seen: cash, card and fiado are never pending, or the queue's account stops adding up and
   * confirming one books its money twice.
   */
  for (const x of p.pagos) {
    if (x.forma === 'efectivo_dolares' && x.montoOriginal === undefined) {
      return rechazo('Un pago en dólares dice cuántos dólares fueron');
    }
    if (x.estado === 'pendiente' && x.forma !== 'transferencia' && x.forma !== 'billetera') {
      return rechazo('Solo una transferencia o un pago por billetera espera a que se confirme');
    }
  }

  /**
   * Only what is there (`venderSoloLoQueHay`, 2026-10-08): a till that could reach the server asks
   * for the sale to be refused if what it takes is not free, and the server's order decides between
   * two tills selling the last of something before either prints a receipt. The owner's yes goes
   * past it. A sale sent without the question, offline or as the copy of one never answered
   * (`respaldo`), is never refused for its stock: the goods have left.
   */
  if (p.soloSiHay && !p.faltaAutorizadaPor && p.ubicacion === DONDE_SE_APARTA) {
    const falta = await noHay(p, read, config.apartarPedidos);
    if (falta) return rechazo(falta);
  }

  const effects: Effect[] = [];

  // --- Inventario y costo ---
  const pendiente = new Map<string, { cantidad: Milli; costoPromedio: UnitCost }>();
  /** Total base quantity leaving each product+location, for the batch draw-down below. */
  const consumoPorProducto = new Map<string, Milli>();
  /** Stock a count already showed this sale gone from: it stays where the count left it. */
  const contados = new Set<string>();
  const lineas: VentaLinea[] = [];
  let costoTotal: Centavos = ZERO;
  let costoContado: Centavos = ZERO;
  let subtotal: Centavos = ZERO;

  for (const l of p.lineas) {
    let costoUnitario: UnitCost = ZERO_COST;
    let costoLinea: Centavos = ZERO;

    if (l.productoId) {
      const baseQty = saleToBase(l.cantidad as Milli, l.factor as Milli);
      const clave = existenciaId(l.productoId, p.ubicacion);

      let estado = pendiente.get(clave);
      if (!estado) {
        const actual = await read.existencia(l.productoId, p.ubicacion);
        if (yaContado(actual, event)) contados.add(clave);
        estado = {
          cantidad: actual?.cantidad ?? ZERO_QTY,
          // Only a shelf with nothing on it takes another place's cost: units on it at no cost are
          // worth nothing on the books, and taking a cost for them made them worth something with
          // no entry saying so (2026-10-13).
          costoPromedio:
            actual && (actual.costoPromedio > 0 || actual.cantidad !== 0)
              ? actual.costoPromedio
              : await costoDeOtroLugar(read, l.productoId),
        };
      }

      costoUnitario = estado.costoPromedio;
      costoLinea = costOf(costoUnitario, baseQty);

      // Quantity drops; the average does not move.
      pendiente.set(clave, {
        cantidad: subQty(estado.cantidad, baseQty),
        costoPromedio: estado.costoPromedio,
      });

      consumoPorProducto.set(clave, addQty(consumoPorProducto.get(clave) ?? ZERO_QTY, baseQty));
      costoTotal = addMoney(costoTotal, costoLinea);
      if (contados.has(clave)) costoContado = addMoney(costoContado, costoLinea);
    }

    subtotal = addMoney(subtotal, centavos(l.subtotal));
    lineas.push({
      productoId: l.productoId,
      descripcion: l.descripcion,
      unidadVentaId: l.unidadVentaId,
      cantidad: l.cantidad as Milli,
      factor: l.factor as Milli,
      precioLista: centavos(l.precioLista),
      // Only when the line went at another list than the sale's: the rest read the sale's.
      ...(l.listaId && l.listaId !== p.listaId ? { listaId: l.listaId } : {}),
      precioUnitario: centavos(l.precioUnitario),
      motivoOverride: l.motivoOverride ?? null,
      autorizadoPor: l.autorizadoPor ?? null,
      // The offer it was charged at, and the list's own price: what sold on an offer is told apart.
      ...(l.ofertaId ? { ofertaId: l.ofertaId, ...(l.precioRegular !== undefined ? { precioRegular: centavos(l.precioRegular) } : {}) } : {}),
      subtotal: centavos(l.subtotal),
      costoPosteado: costoLinea,
      costoUnitarioPosteado: costoUnitario,
    });
  }

  /**
   * Stock is allowed to go negative. The customer is already holding the rice; refusing the
   * sale because a count says zero only teaches staff to work around the system. It lands on
   * a review list instead.
   */
  for (const [clave, estado] of pendiente) {
    if (contados.has(clave)) continue;
    const [productoId, ubicacion] = partirClave(clave);
    effects.push({
      kind: 'existencia.set',
      id: clave,
      row: {
        id: clave,
        productoId,
        ubicacion,
        cantidad: estado.cantidad,
        costoPromedio: estado.costoPromedio,
        ultimoMovimientoId: p.ventaId,
        rev,
      },
    });
  }

  /**
   * Batches go out earliest-expiry first.
   *
   * Only products flagged as medicine or perishable have batches at all, so for most of the
   * catalog this finds nothing and costs one lookup. For the ones that do, it is what keeps the
   * expiry board honest and what makes a recall answerable: which batch went out, and when.
   */
  for (const [clave, consumido] of consumoPorProducto) {
    if (contados.has(clave)) continue;
    const [productoId, ubicacion] = partirClave(clave);
    const lotes = await read.lotesDe(productoId, ubicacion);
    effects.push(...consumirLotesFefo(lotes, consumido, rev));
  }

  // --- Pagos ---
  const descuento = centavos(p.descuento);
  const envio = centavos(p.envio);
  const redondeo = centavos(p.redondeo);
  const cambio = centavos(p.cambio);
  const total = centavos(p.total);

  const pagos: VentaPago[] = [];
  const lineasAsiento: JournalLine[] = [];
  let fxDiff = 0;
  let efectivoCordobas: Centavos = ZERO;
  let efectivoDolares: Centavos = ZERO;
  /** What those dollars went into «Caja dólares» at, which is what the corte takes back out. */
  let dolaresContables: Centavos = ZERO;
  let otrosPagos: Centavos = ZERO;
  /**
   * A transfer or a wallet payment taken as already seen is the owner's to take so: anybody else's
   * waits in the queue like any other (2026-10-13). The till never sends one confirmed, but the
   * books took one from anybody, money in the bank on a cashier's word, past the queue that
   * `pago.confirmado` keeps hers out of.
   */
  const confirma = await confirmaTransferencias(read, event.actorId);

  for (const original of p.pagos) {
    const sinVer = (original.forma === 'transferencia' || original.forma === 'billetera') && original.estado === 'confirmado' && !confirma;
    const { cuenta: _cuenta, ...sinCuenta } = original;
    const pago = sinVer ? { ...sinCuenta, estado: 'pendiente' as const } : original;
    const monto = centavos(pago.monto);
    let montoContable = monto;

    if (pago.forma === 'efectivo_dolares' && pago.montoOriginal) {
      /**
       * Dollars enter the drawer valued at the OFFICIAL rate, while the customer was credited
       * at the store rate. Isolating that gap as FX gain or loss is what keeps an exchange
       * spread from quietly showing up as product margin.
       */
      const dolares = centavos(pago.montoOriginal);
      montoContable = usdToCordobas(dolares, fxRate(config.fxTasaOficial));
      fxDiff += montoContable - monto;
      efectivoDolares = addMoney(efectivoDolares, dolares);
      dolaresContables = addMoney(dolaresContables, montoContable);
    } else if (pago.forma === 'efectivo_cordobas') {
      efectivoCordobas = addMoney(efectivoCordobas, monto);
    } else if (pago.forma !== 'credito') {
      otrosPagos = addMoney(otrosPagos, monto);
    }

    lineasAsiento.push(debit(cuentaDePago(pago.forma, pago.estado, pago.cuenta), montoContable));
    pagos.push({
      forma: pago.forma,
      monto,
      montoContable,
      montoOriginal: pago.montoOriginal ? centavos(pago.montoOriginal) : null,
      tasaCambio: pago.tasaCambio ?? null,
      referencia: pago.referencia ?? null,
      estado: pago.estado,
      ...(pago.autorizadoPor ? { autorizadoPor: pago.autorizadoPor } : {}),
      ...(pago.cuenta ? { cuenta: pago.cuenta } : {}),
    });
  }

  if (cambio > 0) lineasAsiento.push(credit('CAJA_CORDOBAS', cambio, 'Vuelto'));
  if (descuento > 0) lineasAsiento.push(debit('DESCUENTOS', descuento));
  lineasAsiento.push(credit('VENTAS', subtotal, p.ticket));
  if (envio > 0) lineasAsiento.push(credit('ENVIOS', envio, 'Envío'));
  if (redondeo > 0) lineasAsiento.push(credit('REDONDEO', redondeo));
  if (redondeo < 0) lineasAsiento.push(debit('REDONDEO', centavos(-redondeo)));
  if (fxDiff > 0) lineasAsiento.push(credit('GANANCIA_CAMBIARIA', centavos(fxDiff)));
  if (fxDiff < 0) lineasAsiento.push(debit('PERDIDA_CAMBIARIA', centavos(-fxDiff)));

  effects.push({
    kind: 'asiento',
    entry: entry(event.id, p.fecha, `Venta ${p.ticket}`, lineasAsiento),
  });

  if (costoTotal > 0) {
    effects.push({
      kind: 'asiento',
      entry: entry(event.id, p.fecha, `Costo de venta ${p.ticket}`, [
        debit('COSTO_VENTAS', costoTotal),
        // Relieved from wherever the goods actually were. Selling off the truck and crediting
        // the warehouse would leave the truck's balance permanently overstated.
        ...(costoTotal > costoContado ? [credit(cuentaInventario(p.ubicacion), subMoney(costoTotal, costoContado))] : []),
        ...(costoContado > 0 ? [credit('FALTANTE_INVENTARIO', costoContado, 'Ya contado')] : []),
      ]),
    });
  }

  const venta: Venta = {
    id: p.ventaId,
    ticket: p.ticket,
    sesionId: p.sesionId,
    fecha: p.fecha,
    ubicacion: p.ubicacion,
    listaId: p.listaId,
    clienteId: p.clienteId,
    pedidoId: p.pedidoId,
    lineas,
    descuento,
    ...(envio > 0 ? { envio } : {}),
    redondeo,
    total,
    pagos,
    cambio,
    costoTotal,
    anulada: false,
    motivoAnulacion: null,
    rev,
  };
  effects.push({ kind: 'venta.upsert', id: p.ventaId, row: venta });

  effects.push({
    kind: 'sesion.patch',
    id: p.sesionId,
    rev,
    patch: {
      efectivoCordobas: addMoney(sesion.efectivoCordobas, efectivoCordobas),
      efectivoDolares: addMoney(sesion.efectivoDolares, efectivoDolares),
      ...(dolaresContables > 0
        ? { dolaresContables: addMoney(sesion.dolaresContables ?? ZERO, dolaresContables) }
        : {}),
      otrosPagos: addMoney(sesion.otrosPagos, otrosPagos),
      cambioEntregado: addMoney(sesion.cambioEntregado, cambio),
      ventas: sesion.ventas + 1,
      totalVendido: addMoney(sesion.totalVendido, total),
      costoVendido: addMoney(sesion.costoVendido, costoTotal),
      ...(fiado > 0 ? { fiado: addMoney(sesion.fiado ?? ZERO, centavos(fiado)) } : {}),
    },
  });

  if (cliente) {
    effects.push({
      kind: 'cliente.patch',
      id: cliente.id,
      rev,
      patch: { saldo: addMoney(cliente.saldo ?? ZERO, centavos(fiado)) },
    });
  }

  if (pedido) {
    effects.push({
      kind: 'pedido.patch',
      id: pedido.id,
      rev,
      patch: { estado: 'entregado', ventaId: p.ventaId },
    });
  }

  return { effects, rejections: [] };
}

async function anular(
  event: Extract<AnyDomainEvent, { type: 'venta.anulada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const { ventaId, motivo, reingresarStock, sesionId: cajaId } = event.payload;

  const venta = await read.venta(ventaId);
  if (!venta) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa venta no existe' }] };
  }
  if (venta.anulada) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa venta ya está anulada' }] };
  }
  /**
   * Part of it already came back and was refunded. Voiding the rest would refund that part a
   * second time, so what is left goes back as a return too.
   */
  if ((venta.devuelto ?? 0) > 0 || venta.lineas.some((l) => (l.devuelta ?? 0) > 0)) {
    return {
      effects: [],
      rejections: [
        { eventId: event.id, reason: 'Esa venta ya tiene devoluciones. Devuelva lo que queda en vez de anularla.' },
      ],
    };
  }

  /**
   * Where the customer's cash comes back from. While the sale's own till is open, from that
   * drawer, the way it went in. Once that till is closed its money has gone to general cash with
   * the corte, so the cash is handed back out of a till that is open now, and that till's corte
   * expects it gone: a void has to leave a drawer the same way a return does. Nothing in cash,
   * no till needed.
   */
  const sesion = await read.sesionCaja(venta.sesionId);
  const turnoAbierto = sesion?.estado === 'abierta';
  const efectivo = efectivoDeVenta(venta);
  const caja = !turnoAbierto && efectivo > 0 && cajaId ? await read.sesionCaja(cajaId) : undefined;
  if (!turnoAbierto && efectivo > 0) {
    if (!caja) {
      return {
        effects: [],
        rejections: [
          { eventId: event.id, reason: 'Esa venta es de un turno cerrado: su efectivo se devuelve de una caja abierta' },
        ],
      };
    }
    if (caja.estado !== 'abierta') {
      return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja de este reembolso ya fue cerrada' }] };
    }
  }

  const effects: Effect[] = [];
  const donde = adondeVuelve(venta, sesion, caja);
  /**
   * The day the void goes on the books: the sale's while its shift is still open, and the sale
   * never happened. Once that shift has closed, its day was counted and read, and the void is
   * today's news, as a return is: its cash leaves today's drawer today. It used to go on the sale's
   * day, and money handed back from this morning's till showed in last month's cash (2026-10-13).
   */
  const fecha = turnoAbierto ? venta.fecha : fechaEnNicaragua(event.clientTs);

  /** Goods a count already found back on the shelf: they stay where the count left them. */
  const contados = new Set<string>();
  let costoContado: Centavos = ZERO;
  /** What each stock line took back, for what stock below zero cost beyond what was booked. */
  const reingresos = new Map<string, Recibido>();
  if (reingresarStock) {
    const pendiente = new Map<string, { cantidad: Milli; costoPromedio: UnitCost }>();
    for (const l of venta.lineas) {
      if (!l.productoId) continue;
      const baseQty = saleToBase(l.cantidad, l.factor);
      const clave = existenciaId(l.productoId, donde);

      let estado = pendiente.get(clave);
      if (!estado) {
        const actual = await read.existencia(l.productoId, donde);
        if (yaContado(actual, event)) contados.add(clave);
        estado = {
          cantidad: actual?.cantidad ?? ZERO_QTY,
          costoPromedio: actual?.costoPromedio ?? ZERO_COST,
        };
      }
      if (contados.has(clave)) {
        costoContado = addMoney(costoContado, l.costoPosteado);
        continue;
      }

      // Goods come back at the cost they left with, blended into whatever the average is now.
      const despues = {
        cantidad: addQty(estado.cantidad, baseQty),
        costoPromedio: weightedAverageCost(estado.cantidad, estado.costoPromedio, baseQty, l.costoUnitarioPosteado),
      };
      pendiente.set(clave, despues);
      const r = reingresos.get(clave);
      if (r) {
        r.despues = despues;
        r.entrante = addMoney(r.entrante, l.costoPosteado);
      } else {
        reingresos.set(clave, { antes: estado, despues, entrante: l.costoPosteado, cuenta: cuentaInventario(donde) });
      }
    }

    for (const [clave, estado] of pendiente) {
      const [productoId, ubicacion] = partirClave(clave);
      effects.push({
        kind: 'existencia.set',
        id: clave,
        row: {
          id: clave,
          productoId,
          ubicacion,
          cantidad: estado.cantidad,
          costoPromedio: estado.costoPromedio,
          ultimoMovimientoId: event.id,
          rev,
        },
      });
    }

    // Batches come back too, newest-expiry last — the mirror of how they went out.
    for (const l of venta.lineas) {
      if (!l.productoId || contados.has(existenciaId(l.productoId, donde))) continue;
      const lotes = await read.lotesDe(l.productoId, donde);
      effects.push(...devolverLotes(lotes, saleToBase(l.cantidad, l.factor), rev));
    }
  }

  /**
   * Mirror the original entry, swapped. Reconstructed from the stored payments so a dollar
   * payment reverses at the same book value it entered with. From a till already closed, the
   * cash goes back in córdobas out of the open one instead, net of the change: the dollars stay
   * in general cash, bought at the shop's rate, and so does the gain on them.
   */
  const lineasAsiento: JournalLine[] = [];
  let fxDiff = 0;
  for (const pago of venta.pagos) {
    /**
     * A transfer that never came was booked as lost (`pago.rechazado`). The goods coming back
     * undo the loss: nothing was lost on a sale that did not happen. Skipped, it left the void
     * short by that much, and an entry that does not balance throws — refusing the void on this
     * phone and, when it reached the server first, every push from the phone after it.
     */
    if (pago.estado === 'rechazado') {
      lineasAsiento.push(credit('TRANSFERENCIAS_PERDIDAS', pago.montoContable, 'Nunca llegó; la mercadería volvió'));
      fxDiff += pago.montoContable - pago.monto;
      continue;
    }
    if (caja && (pago.forma === 'efectivo_cordobas' || pago.forma === 'efectivo_dolares')) continue;
    lineasAsiento.push(credit(cuentaDePago(pago.forma, pago.estado, pago.cuenta), pago.montoContable));
    fxDiff += pago.montoContable - pago.monto;
  }
  if (caja) lineasAsiento.push(credit('CAJA_CORDOBAS', efectivo, 'Devuelto de la caja abierta'));
  else if (venta.cambio > 0) lineasAsiento.push(debit('CAJA_CORDOBAS', venta.cambio, 'Vuelto devuelto'));
  if (venta.descuento > 0) lineasAsiento.push(credit('DESCUENTOS', venta.descuento));
  lineasAsiento.push(debit('VENTAS', subtotalDe(venta), `Anulación ${venta.ticket}`));
  // The trip is undone with the sale: the customer gets back what they paid for it too.
  if ((venta.envio ?? 0) > 0) lineasAsiento.push(debit('ENVIOS', venta.envio!, 'Envío'));
  if (venta.redondeo > 0) lineasAsiento.push(debit('REDONDEO', venta.redondeo));
  if (venta.redondeo < 0) lineasAsiento.push(credit('REDONDEO', centavos(-venta.redondeo)));
  if (fxDiff > 0) lineasAsiento.push(debit('GANANCIA_CAMBIARIA', centavos(fxDiff)));
  if (fxDiff < 0) lineasAsiento.push(credit('PERDIDA_CAMBIARIA', centavos(-fxDiff)));

  effects.push({
    kind: 'asiento',
    entry: entry(event.id, fecha, `Anulación de ${venta.ticket}: ${motivo}`, lineasAsiento),
  });

  if (venta.costoTotal > 0) {
    effects.push({
      kind: 'asiento',
      entry: entry(
        event.id,
        fecha,
        reingresarStock
          ? `Reingreso por anulación ${venta.ticket}`
          : `Producto no reingresado ${venta.ticket}`,
        // Not restocking means the goods are gone: the expense moves from cost of sales to
        // merma, where it reads as a loss rather than as something that was sold.
        [
          ...(!reingresarStock
            ? [debit('MERMA', venta.costoTotal)]
            : [
                ...(venta.costoTotal > costoContado
                  ? [debit(cuentaInventario(donde), subMoney(venta.costoTotal, costoContado))]
                  : []),
                ...(costoContado > 0 ? [debit('FALTANTE_INVENTARIO', costoContado, 'Ya contado')] : []),
              ]),
          credit('COSTO_VENTAS', venta.costoTotal),
        ],
      ),
    });
  }
  const ajuste = ajusteVendidoAntes(event.id, fecha, [...reingresos.values()]);
  if (ajuste) effects.push(ajuste);

  effects.push({
    kind: 'venta.patch',
    id: ventaId,
    rev,
    patch: { anulada: true, motivoAnulacion: motivo },
  });

  if (caja) {
    effects.push({
      kind: 'sesion.patch',
      id: caja.id,
      rev,
      patch: { devoluciones: addMoney(caja.devoluciones ?? ZERO, efectivo) },
    });
  }
  if (sesion && turnoAbierto) {
    let efC = sesion.efectivoCordobas;
    let efD = sesion.efectivoDolares;
    let contables = sesion.dolaresContables ?? ZERO;
    let otros = sesion.otrosPagos;
    let fiado = sesion.fiado ?? ZERO;
    for (const pago of venta.pagos) {
      // A transfer that never came is still among the shift's other payments (`pago.rechazado`
      // leaves them): it goes with the sale, as what was sold does. Skipped, «Otros medios» kept it,
      // and the day's «En efectivo» came out that much short (2026-10-20).
      if (pago.forma === 'efectivo_dolares' && pago.montoOriginal) {
        efD = subMoney(efD, pago.montoOriginal);
        contables = subMoney(contables, pago.montoContable);
      } else if (pago.forma === 'efectivo_cordobas') {
        efC = subMoney(efC, pago.monto);
      } else if (pago.forma === 'credito') {
        fiado = subMoney(fiado, pago.monto);
      } else {
        otros = subMoney(otros, pago.monto);
      }
    }
    effects.push({
      kind: 'sesion.patch',
      id: venta.sesionId,
      rev,
      patch: {
        efectivoCordobas: efC,
        efectivoDolares: efD,
        ...(contables !== (sesion.dolaresContables ?? ZERO) ? { dolaresContables: contables } : {}),
        otrosPagos: otros,
        cambioEntregado: subMoney(sesion.cambioEntregado, venta.cambio),
        ventas: Math.max(0, sesion.ventas - 1),
        totalVendido: subMoney(sesion.totalVendido, venta.total),
        costoVendido: subMoney(sesion.costoVendido, venta.costoTotal),
        ...(fiado !== (sesion.fiado ?? ZERO) ? { fiado } : {}),
      },
    });
  }

  // A voided fiado is not owed. If some of it was already paid off, the client ends up with
  // money in their favour, which is what happened; if it was given up on, it is not lost either
  // (`bajarDeuda`), from the day of the void.
  const fiadoAnulado = fiadoDeVenta(venta);
  if (fiadoAnulado > 0 && venta.clienteId) {
    const cliente = await read.cliente(venta.clienteId);
    if (cliente) {
      const baja = bajarDeuda(cliente, fiadoAnulado, event.id, fechaEnNicaragua(event.clientTs), `se anuló ${venta.ticket}`);
      effects.push({ kind: 'cliente.patch', id: cliente.id, rev, patch: baja.patch });
      if (baja.asiento) effects.push(baja.asiento);
    }
  }

  // The order goes back to pending: voiding the sale un-delivers it, it does not erase it.
  if (venta.pedidoId) {
    const pedido = await read.pedido(venta.pedidoId);
    // Its own delivery: an order another sale delivered stays delivered.
    if (pedido && pedido.estado === 'entregado' && (pedido.ventaId ?? venta.id) === venta.id) {
      effects.push({
        kind: 'pedido.patch',
        id: venta.pedidoId,
        rev,
        patch: { estado: 'pendiente', ventaId: null },
      });
    }
  }

  return { effects, rejections: [] };
}

async function confirmarPago(
  event: Extract<AnyDomainEvent, { type: 'pago.confirmado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const { recargaId, abonoId, referencia, cuenta } = event.payload;
  const config = await read.config();
  const cerrada = cuentaCerradaParaSiempre(config, cuenta);
  if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
  // The day it was seen, for one that came in after a statement already squared (`diaDeLaConfirmacion`).
  const visto = fechaEnNicaragua(event.clientTs);
  if (recargaId) return confirmarPagoRecarga(event.id, recargaId, referencia, read, rev, visto, cuenta);
  if (abonoId) return confirmarPagoAbono(event.id, abonoId, referencia, read, rev, visto, cuenta);
  // A sale's, then: the schema lets through one or the other, never neither.
  const ventaId = event.payload.ventaId!;
  const indice = event.payload.indice!;
  const venta = await read.venta(ventaId);
  const pago = venta?.pagos[indice];
  if (!venta || !pago) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese pago no existe' }] };
  }
  if (pago.estado !== 'pendiente') {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese pago ya fue resuelto' }] };
  }
  /**
   * Voiding the sale took the transfer out of the queue (`anular`), and the queue no longer shows
   * it. A phone that had not heard of the void yet still can: taken, the same money left the queue
   * twice, and the bank gained what the void had already given up.
   */
  if (venta.anulada) {
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: 'Esa venta se anuló: si el dinero llegó, hay que devolvérselo al cliente' }],
    };
  }

  const billetera = pago.forma === 'billetera';
  // A transfer, into the account it was seen in; a wallet payment, into the wallet.
  const banco = billetera ? null : cuentaDeTransferencia(cuenta);
  const pagos = venta.pagos.map((x, i) =>
    i === indice
      ? { ...x, estado: 'confirmado' as const, referencia: referencia ?? x.referencia, ...(banco && cuenta ? { cuenta: banco } : {}) }
      : x,
  );

  return {
    effects: [
      { kind: 'venta.patch', id: ventaId, rev, patch: { pagos } },
      {
        kind: 'asiento',
        entry: entry(
          event.id,
          diaDeLaConfirmacion(config, banco ?? 'BILLETERA_MOVIL', venta.fecha, visto),
          `${billetera ? 'Pago por billetera confirmado' : 'Transferencia confirmada'} ${venta.ticket}`,
          [
            debit(banco ?? 'BILLETERA_MOVIL', pago.montoContable, referencia ?? ''),
            credit('TRANSFERENCIAS_PENDIENTES', pago.montoContable),
          ],
        ),
      },
    ],
    rejections: [],
  };
}

async function rechazarPago(
  event: Extract<AnyDomainEvent, { type: 'pago.rechazado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const { recargaId, abonoId, motivo, aFiado } = event.payload;
  if (recargaId) {
    if (aFiado) {
      return { effects: [], rejections: [{ eventId: event.id, reason: 'Una recarga no tiene cliente: no hay cuenta a la cual pasarla' }] };
    }
    return rechazarPagoRecarga(event.id, recargaId, motivo, read, rev);
  }
  // An abono's goes back on the client's account by itself: that is what it not arriving means.
  if (abonoId) return rechazarPagoAbono(event.id, abonoId, motivo, read, rev);
  const ventaId = event.payload.ventaId!;
  const indice = event.payload.indice!;
  const venta = await read.venta(ventaId);
  const pago = venta?.pagos[indice];
  if (!venta || !pago) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese pago no existe' }] };
  }
  if (pago.estado !== 'pendiente') {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese pago ya fue resuelto' }] };
  }
  // As with confirming it: the void already took it out of the queue, so nothing is lost.
  if (venta.anulada) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa venta se anuló: ese pago ya no se espera' }] };
  }
  if (aFiado) return pasarAFiado(event.id, venta, indice, motivo, read, rev);

  const pagos = venta.pagos.map((x, i) => (i === indice ? { ...x, estado: 'rechazado' as const } : x));

  /**
   * The sale stands and the goods are gone; only the money never arrived. Moving it to an
   * expense account named for exactly that keeps it visible instead of letting it sit forever
   * in a holding account that slowly stops meaning anything.
   */
  return {
    effects: [
      { kind: 'venta.patch', id: ventaId, rev, patch: { pagos } },
      {
        kind: 'asiento',
        entry: entry(event.id, venta.fecha, `${pago.forma === 'billetera' ? 'Pago por billetera' : 'Transferencia'} no cobrada ${venta.ticket}: ${motivo}`, [
          debit('TRANSFERENCIAS_PERDIDAS', pago.montoContable, motivo),
          credit('TRANSFERENCIAS_PENDIENTES', pago.montoContable),
        ]),
      },
    ],
    rejections: [],
  };
}

/**
 * A transfer that never arrived, from a client the shop knows: instead of a loss, what they owe,
 * on their account like any fiado — the owner's call, so past their limit too. The payment turns
 * into fiado (`era` says how it started), so the statement, a later abono and a void of the sale
 * all treat it as the fiado it now is.
 */
async function pasarAFiado(
  eventId: string,
  venta: Venta,
  indice: number,
  motivo: string,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const pago = venta.pagos[indice]!;
  const cliente = venta.clienteId ? await read.cliente(venta.clienteId) : undefined;
  if (!cliente) {
    return { effects: [], rejections: [{ eventId, reason: 'Esa venta no tiene cliente: no hay cuenta a la cual pasarlo' }] };
  }
  const pagos = venta.pagos.map((x, i) =>
    i === indice
      ? { ...x, forma: 'credito' as const, estado: 'confirmado' as const, era: pago.forma as 'transferencia' | 'billetera' }
      : x,
  );
  const effects: Effect[] = [
    { kind: 'venta.patch', id: venta.id, rev, patch: { pagos } },
    { kind: 'cliente.patch', id: cliente.id, rev, patch: { saldo: addMoney(cliente.saldo ?? ZERO, pago.monto) } },
    {
      kind: 'asiento',
      entry: entry(eventId, venta.fecha, `Transferencia no cobrada ${venta.ticket}, a la cuenta de ${cliente.nombre}: ${motivo}`, [
        debit('CUENTAS_POR_COBRAR', pago.montoContable, motivo),
        credit('TRANSFERENCIAS_PENDIENTES', pago.montoContable),
      ]),
    },
  ];
  // Still the open shift: its summary moves the amount from other payments to fiado.
  const sesion = await read.sesionCaja(venta.sesionId);
  if (sesion?.estado === 'abierta') {
    effects.push({
      kind: 'sesion.patch',
      id: sesion.id,
      rev,
      patch: { otrosPagos: subMoney(sesion.otrosPagos, pago.monto), fiado: addMoney(sesion.fiado ?? ZERO, pago.monto) },
    });
  }
  return { effects, rejections: [] };
}

/**
 * What a product costs where the till has none: stock received into the storeroom and sold at the
 * counter before anybody moved it across. Taken from wherever else it has a cost, the location
 * holding the most. Without it the sale booked its cost at nothing — the profit came out too
 * high, and the counter's stock carried no value to set against it when the goods did come over.
 * Kept as that location's average from then on, so what the stock is worth and what the books
 * say still agree.
 */
async function costoDeOtroLugar(read: Reader, productoId: string): Promise<UnitCost> {
  const conCosto = (await read.existenciasDeProducto(productoId)).filter((e) => e.costoPromedio > 0);
  // Two holding as much: the same one on every device, whatever order its database lists them in.
  conCosto.sort((a, b) => b.cantidad - a.cantidad || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return conCosto[0]?.costoPromedio ?? ZERO_COST;
}

function subtotalDe(v: Venta): Centavos {
  return centavos(v.lineas.reduce<number>((a, l) => a + l.subtotal, 0));
}

function partirClave(clave: string): [string, string] {
  const corte = clave.indexOf(':');
  return [clave.slice(0, corte), clave.slice(corte + 1)];
}
